import { Typography } from '@euler/app/components/typography';
import {
  CardLoading,
  CardNotice,
} from '@euler/app/flows/ai-suite/components/ChatCard';
import { aiColors, tint } from '@euler/app/flows/ai-suite/theme';
import { BiTag } from '@euler/app/flows/bi-dashboards/components/ui/BiTag';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { Check, CircleCheck, Maximize2 } from 'lucide-react-native';
import { memo, useState } from 'react';
import { Pressable, View } from 'react-native';
import { BiVisualizationBody } from '../bi-visualization/BiVisualizationBody';
import { useBiDraft } from '../bi-visualization/functions/useBiDraft';
import { ProposalFullPanel } from './ProposalFullPanel';

/** A preview is cut at this height; the full chart opens in a panel. */
const kPreviewHeight = 280;

/**
 * One proposal: its title, why it was picked, a small preview and a
 * checkbox; **查看大图** opens it full size.
 */
export const ProposalTile = memo(
  ({
    draftId,
    selectable,
    selected,
    onToggle,
  }: {
    draftId: string;
    /** Outside a conversation there is nowhere to add it. */
    selectable: boolean;
    selected: boolean;
    onToggle: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const state = useBiDraft(draftId);
    const [full, setFull] = useState(false);
    const draft = state.kind === 'ready' ? state.draft : null;
    const [placed] = draft?.addedTo ?? [];
    const checkable = selectable && !!draft;
    return (
      <View
        style={{
          gap: rpx(8),
          padding: rpx(12),
          borderRadius: rpx(12),
          borderWidth: 1,
          borderColor: selected ? aiColors.primary : aiColors.border,
          backgroundColor: selected
            ? tint(aiColors.primary, 0.04)
            : aiColors.card,
        }}
      >
        <Pressable
          accessibilityRole="checkbox"
          accessibilityLabel={draft ? `选择「${draft.title}」` : '推荐图表'}
          accessibilityState={{ checked: selected, disabled: !checkable }}
          disabled={!checkable}
          onPress={onToggle}
          style={{
            flexDirection: 'row',
            alignItems: 'flex-start',
            gap: rpx(8),
          }}
        >
          {selectable ? (
            <View
              style={{
                width: rpx(18),
                height: rpx(18),
                marginTop: rpx(1),
                borderRadius: rpx(5),
                borderWidth: 1.5,
                borderColor: selected ? aiColors.primary : aiColors.border,
                backgroundColor: selected ? aiColors.primary : aiColors.card,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {selected ? (
                <Check
                  size={12}
                  color={aiColors.primaryForeground}
                  strokeWidth={3}
                />
              ) : null}
            </View>
          ) : null}
          <View style={{ flex: 1, gap: rpx(3) }}>
            <Typography.Text
              medium
              size={rpx(13)}
              color={aiColors.foreground}
              numberOfLines={2}
            >
              {draft?.title ?? '推荐图表'}
            </Typography.Text>
            {draft?.rationale ? (
              <Typography.Text
                size={rpx(11)}
                lineHeight={rpx(16)}
                color={aiColors.mutedForeground}
                numberOfLines={2}
              >
                {draft.rationale}
              </Typography.Text>
            ) : null}
          </View>
        </Pressable>
        {state.kind === 'loading' ? (
          <CardLoading label="正在读取数据…" />
        ) : state.kind === 'expired' ? (
          <CardNotice text={state.message} />
        ) : state.kind === 'missing' ? (
          <CardNotice text="找不到这张图表。" />
        ) : state.kind === 'failed' ? (
          <CardNotice tone="danger" text="图表没有加载出来，请稍后重试。" />
        ) : (
          <View
            pointerEvents="none"
            style={{ maxHeight: rpx(kPreviewHeight), overflow: 'hidden' }}
          >
            <BiVisualizationBody
              viz={state.draft.viz}
              result={state.draft.result}
            />
          </View>
        )}
        {draft ? (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: rpx(8),
            }}
          >
            {placed ? (
              <BiTag
                text={`已加到「${placed.dashboardName}」`}
                color={aiColors.green}
                icon={CircleCheck}
              />
            ) : (
              <View />
            )}
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={`查看大图「${draft.title}」`}
              onPress={() => setFull(true)}
              hitSlop={6}
              style={({ pressed }) => ({
                flexDirection: 'row',
                alignItems: 'center',
                gap: rpx(4),
                opacity: pressed ? 0.6 : 1,
              })}
            >
              <Maximize2 size={13} color={aiColors.primary} />
              <Typography.Text size={rpx(12)} color={aiColors.primary}>
                查看大图
              </Typography.Text>
            </Pressable>
          </View>
        ) : null}
        {draft ? (
          <ProposalFullPanel
            visible={full}
            draft={draft}
            onClose={() => setFull(false)}
          />
        ) : null}
      </View>
    );
  },
);
